import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Modal, Row, Spin } from 'antd';
import { Check, CheckCircle, InfoOutlined } from '@mui/icons-material';
import { Autocomplete, Button, Checkbox, TextField, Tooltip } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import CustomDateTimePicker from 'components/DateTimePicker';
import debounce from 'lodash/debounce';
import { filter, find, get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'utils/dayjsConfig';
import { useDispatch } from 'react-redux';
import { deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { handleListClassActivities } from 'store/classActivities/thunks';
import { handleListClassQuizzes } from 'store/classQuizzes/thunks';
import { handleListActivities } from 'store/activities/thunks';
import { handleListQuizzes } from 'store/quizzes/thunks';
import { handleSaveStudentDifferentiationBulk } from 'store/studentDifferentiation/thunks';
import { useActivities } from 'store/activities/selectors';
import { useQuizzes } from 'store/quizzes/selectors';
import { useBookUnits } from 'store/booksUnits/selectors';
import * as Sentry from '@sentry/react';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { checkIsDiffForUser, getConfigForDiff, getNonAssignableMaskId, getNumberMask, isVideo } from 'utils/lib/ebookHelper';
import { useUsersByClass } from 'store/userClasses/selectors';
import { useCollectionNotification } from 'utils/methods/firebaseNotification';
import { useFirebase } from 'containers/FirebaseContext';
import { useResources, useVideos } from 'store/resources/selectors';
import { useStory } from 'store/story/selectors';

const draftCollectionId =
  import.meta.env.VITE_APP_STAGE?.trim() === 'prod'
    ? 'student_differentiation_draft_prod'
    : 'student_differentiation_draft';

const emptyRow = () => ({
  rowKey: uuidv4(),
  assignmentValue: null,
  dueDate: null,
  attempts: 1,
  scheduleAt: null,
});

const parseAssignmentValueKey = (assignmentValue) => {
  const rawVal = String(assignmentValue);
  const sep = rawVal.indexOf(':');
  if (sep < 0) {
    return null;
  }
  const kind = rawVal.slice(0, sep);
  const id = rawVal.slice(sep + 1);
  if (kind === 'activity') {
    return { assignmentType: 'activity', assignmentId: id };
  }
  if (kind === 'quiz') {
    return { assignmentType: 'quiz', assignmentId: id };
  }
  return null;
};

const comparableDiffSlice = (dueDate, attempts, scheduleAt) => ({
  due: dueDate ? dayjs(dueDate).minute(0).second(0).millisecond(0).toISOString() : '',
  att: Number(attempts) || 0,
  sch: scheduleAt ? String(scheduleAt) : '',
});

const buildBaselineAndRowsForStudent = (
  studentId,
  classActivities,
  classQuizzes,
  classId,
  booksAllList,
  bookAllUnits,
  activityDefinitionIds,
  quizDefinitionIds,
  resources,
  videos,
  story,
) => {
  const baselineByAssignmentValue = {};
  const rows = [];
  const acts = (classActivities || [])
    .filter((a) => shouldIncludeAssignmentForBulk(a, classId))
    .filter((a) => classActivityMatchesActivitiesCatalog(a, activityDefinitionIds))
    .filter((a) => assignmentHasResolvedBookAndPage(a, booksAllList, bookAllUnits, resources, videos, story));
  for (const raw of acts) {
    if (!assignmentHasDifferentiationForStudents(raw, classId, [studentId])) {
      continue;
    }
    const classConfig = getClassConfigForBulk(raw, classId);
    const cfg = getConfigForDiff(classConfig, studentId);
    if (!cfg || !cfg.dueDateDif) {
      continue;
    }
    const id = raw.id ?? raw.activityId;
    const value = `activity:${id}`;
    const baseRetakes = Number(get(raw, 'retakes', 0)) || 0;
    const retakesDifRaw = get(cfg, 'retakesDif');
    const diffAttemptsParsed =
      retakesDifRaw != null && retakesDifRaw !== '' && Number.isFinite(Number(retakesDifRaw))
        ? Number(retakesDifRaw)
        : null;
    const attempts =
      diffAttemptsParsed != null && diffAttemptsParsed > 0
        ? Math.max(1, diffAttemptsParsed)
        : Math.max(1, baseRetakes);
    const scheduleAt = cfg.scheduleAtDiff || dayjs().toISOString();
    const due = dayjs(cfg.dueDateDif);
    if (!due.isValid()) {
      continue;
    }
    rows.push({
      rowKey: uuidv4(),
      assignmentValue: value,
      dueDate: due,
      attempts,
      scheduleAt,
    });
    baselineByAssignmentValue[value] = comparableDiffSlice(due, attempts, scheduleAt);
  }
  const qz = (classQuizzes || [])
    .filter((q) => shouldIncludeAssignmentForBulk(q, classId))
    .filter((q) => classQuizMatchesQuizzesCatalog(q, quizDefinitionIds))
    .filter((q) => assignmentHasResolvedBookAndPage(q, booksAllList, bookAllUnits, resources, videos, story));
  for (const raw of qz) {
    if (!assignmentHasDifferentiationForStudents(raw, classId, [studentId])) {
      continue;
    }
    const classConfig = getClassConfigForBulk(raw, classId);
    const cfg = getConfigForDiff(classConfig, studentId);
    if (!cfg || !cfg.dueDateDif) {
      continue;
    }
    const id = raw.id ?? raw.quizId;
    const value = `quiz:${id}`;
    const baseRetakes = Number(get(raw, 'retakes', 0)) || 0;
    const retakesDifRaw = get(cfg, 'retakesDif');
    const diffAttemptsParsed =
      retakesDifRaw != null && retakesDifRaw !== '' && Number.isFinite(Number(retakesDifRaw))
        ? Number(retakesDifRaw)
        : null;
    const attempts =
      diffAttemptsParsed != null && diffAttemptsParsed > 0
        ? Math.max(1, diffAttemptsParsed)
        : Math.max(1, baseRetakes);
    const scheduleAt = cfg.scheduleAtDiff || dayjs().toISOString();
    const due = dayjs(cfg.dueDateDif);
    if (!due.isValid()) {
      continue;
    }
    rows.push({
      rowKey: uuidv4(),
      assignmentValue: value,
      dueDate: due,
      attempts,
      scheduleAt,
    });
    baselineByAssignmentValue[value] = comparableDiffSlice(due, attempts, scheduleAt);
  }
  return { rows, baselineByAssignmentValue };
};

const rowsFromDraftPayload = (draftRows, validAssignmentValues) => {
  const valid = new Set(validAssignmentValues);
  const out = [];
  (draftRows || []).forEach((r) => {
    const hasAssignment = Boolean(r?.assignmentValue);
    const hasPartialInputWithoutAssignment =
      !hasAssignment &&
      (Boolean(r?.dueDate) ||
        Boolean(r?.scheduleAt) ||
        r?.attempts === 0 ||
        (r?.attempts != null && r?.attempts !== ''));
    if (hasAssignment && !valid.has(String(r.assignmentValue))) {
      return;
    }
    if (!hasAssignment && !hasPartialInputWithoutAssignment) {
      return;
    }
    const due = r.dueDate ? dayjs(r.dueDate) : null;
    out.push({
      rowKey: uuidv4(),
      assignmentValue: hasAssignment ? r.assignmentValue : null,
      dueDate: due && due.isValid() ? due : null,
      attempts: r.attempts != null && r.attempts !== '' ? Number(r.attempts) : null,
      scheduleAt: r.scheduleAt || null,
    });
  });
  return out;
};

const serializeRowsForDraft = (rows) =>
  (rows || []).map((r) => ({
    assignmentValue: r.assignmentValue,
    dueDate: r.dueDate ? dayjs(r.dueDate).toISOString() : null,
    attempts: r.attempts,
    scheduleAt: r.scheduleAt || null,
  }));

const rowHasAnyUserInput = (row) =>
  Boolean(
    row?.assignmentValue ||
      row?.dueDate ||
      row?.scheduleAt ||
      row?.attempts === 0 ||
      (row?.attempts != null && row?.attempts !== ''),
  );

const buildComparableMapFromRows = (rows) => {
  const out = {};
  (rows || []).forEach((row) => {
    if (!row?.assignmentValue) return;
    out[String(row.assignmentValue)] = comparableDiffSlice(row.dueDate, row.attempts, row.scheduleAt);
  });
  return out;
};

const outlinedUniformSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 56,
    height: 56,
    alignItems: 'center',
  },
  '& .MuiInputBase-root': {
    minHeight: 56,
    height: 56,
    alignItems: 'center',
  },
  '& .MuiInputBase-input': {
    boxSizing: 'border-box',
    paddingTop: 16,
    paddingBottom: 16,
  },
};

const assignmentAutocompleteFieldSx = {
  '& .MuiOutlinedInput-input::placeholder, & .MuiInputBase-input::placeholder': {
    opacity: '0.55 !important',
    color: 'rgba(0, 0, 0, 0.6)',
  },
};

const compactRowFieldSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 42,
    height: 42,
    alignItems: 'center',
  },
  '& .MuiInputBase-root': {
    minHeight: 42,
    height: 42,
    alignItems: 'center',
  },
  '& .MuiInputBase-input': {
    boxSizing: 'border-box',
    paddingTop: 10,
    paddingBottom: 10,
  },
  '& .MuiAutocomplete-inputRoot': {
    minHeight: 42,
    height: 42,
    paddingTop: '4px !important',
    paddingBottom: '4px !important',
    paddingRight: '36px !important',
  },
  '& .MuiAutocomplete-input': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: '6px !important',
  },
  '& .MuiAutocomplete-endAdornment': {
    right: 11,
  },
};

const compactStudentsFieldSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 48,
    height: 48,
    alignItems: 'center',
  },
  '& .MuiInputBase-root': {
    minHeight: 48,
    height: 48,
    alignItems: 'center',
  },
  '& .MuiInputBase-input': {
    boxSizing: 'border-box',
    paddingTop: 11,
    paddingBottom: 11,
  },
};

const rowScrollContainerStyle = {
  maxHeight: 360,
  overflowY: 'auto',
  paddingRight: 4,
};

const modalBodyStyle = {
  overflow: 'visible',
  paddingTop: 14,
};

const rowCardStyle = {
  border: '1px solid #e6e6e6',
  borderRadius: 10,
  padding: 14,
  paddingTop: 5,
  backgroundColor: '#fff',
};

const rowHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 12,
  fontSize: 13,
  fontWeight: 600,
  color: '#4d4d4d',
};

const rowActionButtonSx = {
  minHeight: 40,
  height: 40,
};

const ellipsisTextStyle = {
  display: 'block',
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const truncateLabel = (label, maxLength = 80) => {
  if (!label) {
    return { text: '', truncated: false };
  }
  if (label.length <= maxLength) {
    return { text: label, truncated: false };
  }
  return { text: `${label.slice(0, maxLength - 1)}…`, truncated: true };
};

const assignmentOptionFilter = createFilterOptions({
  stringify: (option) => option.label || '',
});

const buildAssignmentAutocompleteOptions = (getOptionsForRow, rowKey) => {
  const { regularOptions, pastDueOptions } = getOptionsForRow(rowKey);
  const active = regularOptions.map((o) => ({ ...o, groupSortKey: '1' }));
  const past = pastDueOptions.map((o) => ({ ...o, groupSortKey: '2' }));
  return [...active, ...past];
};
const isAssignedToClass = (assignment, classId) => {
  if (!classId || !Array.isArray(assignment?.classes)) {
    return false;
  }
  return assignment.classes.some((c) => String(c.id) === String(classId));
};

const shouldIncludeAssignmentForBulk = (assignment, classId) => {
  if (!classId || !isAssignedToClass(assignment, classId)) {
    return false;
  }
  if (get(assignment, 'is_hidden', false) === true) {
    return false;
  }
  return true;
};

const getClassActivityDefinitionId = (row) =>
  row.activityId ?? get(row, 'data.id') ?? get(row, 'data.activityId');

const getClassQuizDefinitionId = (row) =>
  row.quizId ?? get(row, 'data.id') ?? get(row, 'data.quizId');

const classActivityMatchesActivitiesCatalog = (row, definitionIds) => {
  const defId = getClassActivityDefinitionId(row);
  return defId != null && definitionIds.has(String(defId));
};

const classQuizMatchesQuizzesCatalog = (row, definitionIds) => {
  const defId = getClassQuizDefinitionId(row);
  return defId != null && definitionIds.has(String(defId));
};

const getAssignmentBookPageParts = (item, booksAllList, bookAllUnits, resources, videos, story) => {
  const chapterRaw = get(item, 'data.chapter') ?? get(item, 'bookId');
  const chapter = chapterRaw != null && String(chapterRaw).trim() !== '' ? String(chapterRaw) : '';
  const pageRaw = get(item, 'data.page') ?? get(item, 'pageId') ?? get(item, 'page');
  const pageId = pageRaw != null && String(pageRaw).trim() !== '' ? String(pageRaw) : '';
  const books = Array.isArray(booksAllList) ? booksAllList : [];
  const bookEntry = chapter ? find(books, (b) => String(b.id) === String(chapter)) : null;
  let pagePart, bookName = '';
  bookName = bookEntry?.description || '';
  if (isVideo(chapter, resources)) {
    const videoName = get(find(videos, ['id', chapter]), 'displayName', '');
    pagePart = `Video ${videoName || ''}`;
    bookName = 'Resource';
  }
  if (bookEntry && chapter && pageId) {
    const isStoryBook = Boolean(find(story, ["bookId", chapter]));
    if (bookEntry.isNotAssignable && Array.isArray(bookAllUnits) && bookAllUnits.length > 0 && !isStoryBook) {
      const m = getNonAssignableMaskId(bookAllUnits, item);
      if (m && m.maskId != null) {
        pagePart = m.shortName ? `${m.shortName} Pg. ${m.maskId}` : `Pg. ${m.maskId}`;
      }
    } else {
      const nm = getNumberMask(chapter, pageId, books);
      if (nm != null && nm !== '') {
        pagePart = `Pg. ${nm}`;
      }
    }
  }
  return { bookName, pagePart };
};

const assignmentHasResolvedBookAndPage = (item, booksAllList, bookAllUnits, resources, videos, story) => {
  const { bookName, pagePart } = getAssignmentBookPageParts(item, booksAllList, bookAllUnits, resources, videos, story);
  return Boolean(String(bookName || '').trim() && String(pagePart || '').trim());
};

const buildAssignmentOptionLabel = (item, booksAllList, bookAllUnits, resources, videos, story) => {
  const displayName =
    get(item, 'displayName') ||
    get(item, 'data.displayName') ||
    get(item, 'id') ||
    get(item, 'quizId') ||
    '';
  const { bookName, pagePart } = getAssignmentBookPageParts(item, booksAllList, bookAllUnits, resources, videos, story);
  const tail = [];
  if (bookName) tail.push(`(${bookName})`);
  if (pagePart) tail.push(`(${pagePart})`);
  return tail.length ? `${displayName} ${tail.join(' ')}` : displayName;
};

const NESTED_MODAL_Z_INDEX = 10050;

const getClassConfigForBulk = (assignment, cid) => {
  if (!cid || !Array.isArray(assignment?.classes)) {
    return null;
  }
  return find(assignment.classes, (c) => String(c.id) === String(cid)) || null;
};

const assignmentHasDifferentiationForStudents = (raw, cid, selectedStudentIds) => {
  if (!Array.isArray(selectedStudentIds) || selectedStudentIds.length === 0) {
    return false;
  }
  const classConfig = getClassConfigForBulk(raw, cid);
  if (!classConfig) {
    return false;
  }
  return selectedStudentIds.some((sid) => checkIsDiffForUser(classConfig, sid));
};

const assignmentIsPastDue = (assignment, classId) => {
  const classConfig = getClassConfigForBulk(assignment, classId);
  const dueCandidates = [
    get(classConfig, 'dueDate'),
    get(classConfig, 'dueDateDif'),
    get(assignment, 'dueDate'),
  ];
  const dueValue = dueCandidates.find((value) => value != null && String(value).trim() !== '');
  if (!dueValue) {
    return false;
  }
  const due = dayjs(dueValue);
  if (!due.isValid()) {
    return false;
  }
  return due.isBefore(dayjs());
};

const StudentDifferentiationBulkModal = ({
  open,
  onClose,
  classId,
  currentUserId,
  classUsers,
  classActivities,
  classQuizzes,
  classActivitiesData,
  classQuizzesData,
  booksAllList,
  onCompleted,
  initialStudentIds,
  zIndex = NESTED_MODAL_Z_INDEX,
}) => {
  const dispatch = useDispatch();
  const showAlert = useAlert();
  const { firestore } = useFirebase();
  const resources = useResources();
  const story = useStory();
  const videos = useVideos();
  const usersByClass = useUsersByClass();
  const { createBatchNotificationAssignment } = useCollectionNotification();
  const bookAllUnits = useBookUnits();
  const activitiesCatalog = useActivities();
  const quizzesCatalog = useQuizzes();
  const activityCatalogCount = Array.isArray(activitiesCatalog) ? activitiesCatalog.length : 0;
  const quizCatalogCount = Array.isArray(quizzesCatalog) ? quizzesCatalog.length : 0;
  const activitiesCatalogFetchStartedRef = useRef(false);
  const quizzesCatalogFetchStartedRef = useRef(false);
  const mergedClassActivities = useMemo(() => {
    const first = Array.isArray(classActivities) ? classActivities : [];
    const second = Array.isArray(classActivitiesData) ? classActivitiesData : [];
    return [...first, ...second];
  }, [classActivities, classActivitiesData]);

  const mergedClassQuizzes = useMemo(() => {
    const first = Array.isArray(classQuizzes) ? classQuizzes : [];
    const second = Array.isArray(classQuizzesData) ? classQuizzesData : [];
    return [...first, ...second];
  }, [classQuizzes, classQuizzesData]);

  const [studentIds, setStudentIds] = useState([]);
  const [rows, setRows] = useState([emptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingInitialDraft, setLoadingInitialDraft] = useState(false);
  const [hasExistingDifferentiation, setHasExistingDifferentiation] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [datePickerRenderKey, setDatePickerRenderKey] = useState(0);
  const [assignmentDropdownOpenRowKey, setAssignmentDropdownOpenRowKey] = useState(null);
  const baselineByAssignmentRef = useRef({});
  const seededStudentRef = useRef(null);
  const seedingInFlightRef = useRef(null);
  const initialDraftHydratedRef = useRef(false);
  const draftExistsRef = useRef(false);

  const popperZ = zIndex + 200;
  const tooltipPopperProps = useMemo(
    () => ({
      sx: { zIndex: popperZ + 220 },
    }),
    [popperZ],
  );
  const assignmentAutocompleteSlotProps = useMemo(
    () => ({
      paper: {
        sx: {
          zIndex: popperZ,
          width: 380,
          maxWidth: 380,
          '& .MuiAutocomplete-option': {
            overflow: 'hidden',
          },
        },
      },
      popper: {
        sx: { zIndex: popperZ },
      },
    }),
    [popperZ],
  );

  const students = useMemo(
    () => (Array.isArray(classUsers) ? classUsers.filter((u) => u.role === 'student' && u.active !== false) : []),
    [classUsers],
  );

  const studentOptions = useMemo(
    () =>
      students.map((s) => ({
        id: s.id,
        label: `${s.firstName || ''}${s.lastName ? `, ${s.lastName}` : ''}`.trim(),
      })),
    [students],
  );
  const selectedStudentOption = useMemo(() => {
    if (!Array.isArray(studentIds) || studentIds.length === 0) {
      return null;
    }
    const firstId = studentIds[0];
    return studentOptions.find((option) => String(option.id) === String(firstId)) ?? null;
  }, [studentIds, studentOptions]);

  const activityDefinitionIds = useMemo(() => {
    const s = new Set();
    (activitiesCatalog || []).forEach((a) => {
      if (a?.id != null) {
        s.add(String(a.id));
      }
    });
    return s;
  }, [activitiesCatalog]);

  const quizDefinitionIds = useMemo(() => {
    const s = new Set();
    (quizzesCatalog || []).forEach((q) => {
      if (q?.id != null) {
        s.add(String(q.id));
      }
    });
    return s;
  }, [quizzesCatalog]);

  const assignmentOptions = useMemo(() => {
    const acts = (mergedClassActivities || [])
      .filter((a) => shouldIncludeAssignmentForBulk(a, classId))
      .filter((a) => classActivityMatchesActivitiesCatalog(a, activityDefinitionIds))
      .filter((a) => assignmentHasResolvedBookAndPage(a, booksAllList, bookAllUnits, resources, videos, story))
      .map((a) => {
        const id = a.id ?? a.activityId;
        return {
          label: buildAssignmentOptionLabel(a, booksAllList, bookAllUnits, resources, videos, story),
          value: `activity:${id}`,
          kind: 'activity',
          isPastDue: assignmentIsPastDue(a, classId),
          raw: a,
        };
      });
    const qz = (mergedClassQuizzes || [])
      .filter((q) => shouldIncludeAssignmentForBulk(q, classId))
      .filter((q) => classQuizMatchesQuizzesCatalog(q, quizDefinitionIds))
      .filter((q) => assignmentHasResolvedBookAndPage(q, booksAllList, bookAllUnits, resources, videos, story))
      .map((q) => {
        const id = q.id ?? q.quizId;
        return {
          label: buildAssignmentOptionLabel(q, booksAllList, bookAllUnits, resources, videos, story),
          value: `quiz:${id}`,
          kind: 'quiz',
          isPastDue: assignmentIsPastDue(q, classId),
          raw: q,
        };
      });
    const combined = [...acts, ...qz];
    const withDiffFlag = combined.map((o) => ({
      ...o,
      hasExistingDiff: assignmentHasDifferentiationForStudents(o.raw, classId, studentIds),
    }));
    return withDiffFlag.sort((a, b) => {
      if (a.hasExistingDiff === b.hasExistingDiff) {
        return 0;
      }
      return a.hasExistingDiff ? -1 : 1;
    });
  }, [
    mergedClassActivities,
    mergedClassQuizzes,
    booksAllList,
    bookAllUnits,
    classId,
    activityDefinitionIds,
    quizDefinitionIds,
    studentIds,
  ]);

  const assignmentOptionByValue = useMemo(() => {
    const map = {};
    assignmentOptions.forEach((option) => {
      map[option.value] = option;
    });
    return map;
  }, [assignmentOptions]);

  const getOptionsForRow = useCallback(
    (rowKey) => {
      const currentValue = rows.find((r) => r.rowKey === rowKey)?.assignmentValue;
      const taken = new Set(
        rows.filter((r) => r.rowKey !== rowKey && r.assignmentValue).map((r) => r.assignmentValue),
      );
      const available = assignmentOptions.filter((o) => !taken.has(o.value) || o.value === currentValue);
      return {
        regularOptions: available.filter((o) => !o.isPastDue),
        pastDueOptions: available.filter((o) => o.isPastDue),
      };
    },
    [rows, assignmentOptions],
  );
  useEffect(() => {
    if (!open) {
      seededStudentRef.current = null;
      baselineByAssignmentRef.current = {};
      initialDraftHydratedRef.current = false;
      draftExistsRef.current = false;
      setHasExistingDifferentiation(false);
      setLoadingInitialDraft(false);
      return;
    }
    setSubmitting(false);
    const ids = Array.isArray(initialStudentIds)
      ? initialStudentIds.filter((id) => students.some((s) => s.id === id))
      : [];
    setStudentIds(ids.length ? [ids[0]] : []);
  }, [open, initialStudentIds, students]);

  const hasUnsavedAssignmentChanges = useMemo(() => {
    if (!open || !studentIds?.[0]) return false;
    if ((rows || []).some((row) => rowHasAnyUserInput(row) && !row?.assignmentValue)) return true;
    const baseline = baselineByAssignmentRef.current || {};
    const current = buildComparableMapFromRows(rows);
    const baselineKeys = Object.keys(baseline).sort();
    const currentKeys = Object.keys(current).sort();
    if (baselineKeys.length !== currentKeys.length) return true;
    for (let i = 0; i < baselineKeys.length; i += 1) {
      if (baselineKeys[i] !== currentKeys[i]) return true;
      const key = baselineKeys[i];
      const b = baseline[key] || {};
      const c = current[key] || {};
      if (b.due !== c.due || b.att !== c.att || b.sch !== c.sch) return true;
    }
    return false;
  }, [open, rows, studentIds]);

  const validAssignmentValuesForDraft = useMemo(() => assignmentOptions.map((o) => o.value), [assignmentOptions]);

  useEffect(() => {
    if (!open || !classId || !firestore) {
      return;
    }
    const studentId = studentIds[0];
    if (!studentId) {
      setRows([emptyRow()]);
      baselineByAssignmentRef.current = {};
      setHasExistingDifferentiation(false);
      seededStudentRef.current = null;
      initialDraftHydratedRef.current = false;
      return;
    }
    const hasCatalog =
      ((mergedClassActivities || []).length > 0 || (mergedClassQuizzes || []).length > 0) &&
      (activityDefinitionIds.size > 0 || quizDefinitionIds.size > 0);
    if (!hasCatalog) {
      return;
    }
    if (seededStudentRef.current === studentId) {
      return;
    }
    if (seedingInFlightRef.current === studentId) {
      return;
    }
    seedingInFlightRef.current = studentId;
    let cancelled = false;
    (async () => {
      const showInitialDraftLoader = !initialDraftHydratedRef.current;
      if (showInitialDraftLoader) {
        setLoadingInitialDraft(true);
      }
      try {
        const { rows: serverRows, baselineByAssignmentValue } = buildBaselineAndRowsForStudent(
          studentId,
          mergedClassActivities,
          mergedClassQuizzes,
          classId,
          booksAllList,
          bookAllUnits,
          activityDefinitionIds,
          quizDefinitionIds,
          resources,
          videos,
          story,
        );
        let draftRows = null;
        try {
          const snap = await getDoc(doc(firestore, draftCollectionId, `${classId}_${studentId}`));
          if (snap.exists()) {
            const d = snap.data();
            if (String(d?.updatedByUserId || '') === String(currentUserId || '') && Array.isArray(d?.rows)) {
              draftRows = d.rows;
              draftExistsRef.current = true;
            }
          } else {
            draftExistsRef.current = false;
          }
        } catch (e) {
          Sentry.captureException(e);
        }
        if (cancelled) {
          return;
        }
        baselineByAssignmentRef.current = baselineByAssignmentValue;
        setHasExistingDifferentiation(Object.keys(baselineByAssignmentValue || {}).length > 0);
        if (draftRows && draftRows.length > 0) {
          const fromDraft = rowsFromDraftPayload(draftRows, validAssignmentValuesForDraft);
          if (fromDraft.length > 0) {
            setRows(fromDraft);
            seededStudentRef.current = studentId;
            if (!cancelled) {
              initialDraftHydratedRef.current = true;
            }
            return;
          }
        }
        if (serverRows.length > 0) {
          setRows(serverRows);
        } else {
          setRows([emptyRow()]);
        }
        seededStudentRef.current = studentId;
        if (!cancelled) {
          initialDraftHydratedRef.current = true;
        }
      } finally {
        if (showInitialDraftLoader) {
          setLoadingInitialDraft(false);
        }
        if (seedingInFlightRef.current === studentId) {
          seedingInFlightRef.current = null;
        }
      }
    })();
    return () => {
      cancelled = true;
      if (seedingInFlightRef.current === studentId) {
        seedingInFlightRef.current = null;
      }
    };
  }, [
    open,
    classId,
    firestore,
    studentIds,
    mergedClassActivities,
    mergedClassQuizzes,
    booksAllList,
    bookAllUnits,
    activityDefinitionIds,
    quizDefinitionIds,
    currentUserId,
    validAssignmentValuesForDraft,
    story,
  ]);

  const persistDraftDebounced = useMemo(
    () =>
      debounce((payload) => {
        if (!payload.firestore || !payload.classId || !payload.studentId || !payload.userId) {
          return;
        }
        void (async () => {
          try {
            await setDoc(
              doc(payload.firestore, draftCollectionId, `${payload.classId}_${payload.studentId}`),
              {
                classId: payload.classId,
                studentUserId: payload.studentId,
                updatedByUserId: payload.userId,
                updatedAt: new Date().toISOString(),
                rows: payload.rows,
              },
              { merge: true },
            );
          } catch (e) {
            Sentry.captureException(e);
          }
        })();
      }, 700),
    [],
  );

  useEffect(() => {
    if (!open || !firestore || !classId || !currentUserId || !studentIds[0]) {
      return;
    }
    if (String(seededStudentRef.current || '') !== String(studentIds[0])) {
      return;
    }
    if (hasUnsavedAssignmentChanges) {
      persistDraftDebounced({
        firestore,
        classId,
        studentId: studentIds[0],
        userId: currentUserId,
        rows: serializeRowsForDraft(rows),
      });
      draftExistsRef.current = true;
    } else {
      persistDraftDebounced.cancel();
      if (draftExistsRef.current) {
        void deleteDoc(doc(firestore, draftCollectionId, `${classId}_${studentIds[0]}`)).catch((e) => {
          Sentry.captureException(e);
        });
        draftExistsRef.current = false;
      }
    }
    return () => {
      persistDraftDebounced.cancel();
    };
  }, [open, firestore, classId, currentUserId, studentIds, rows, hasUnsavedAssignmentChanges, persistDraftDebounced]);

  useEffect(() => {
    if (!open || activityCatalogCount > 0) {
      return;
    }
    if (activitiesCatalogFetchStartedRef.current) {
      return;
    }
    activitiesCatalogFetchStartedRef.current = true;
    void dispatch(handleListActivities());
  }, [open, dispatch, activityCatalogCount]);

  useEffect(() => {
    if (!open || quizCatalogCount > 0) {
      return;
    }
    if (quizzesCatalogFetchStartedRef.current) {
      return;
    }
    quizzesCatalogFetchStartedRef.current = true;
    void dispatch(handleListQuizzes());
  }, [open, dispatch, quizCatalogCount]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [open]);

  const addRow = useCallback(() => {
    setRows((r) => [...r, emptyRow()]);
  }, []);

  const updateRow = useCallback((rowKey, patch) => {
    setRows((r) => r.map((row) => (row.rowKey === rowKey ? { ...row, ...patch } : row)));
  }, []);

  const removeRow = useCallback((rowKey) => {
    setRows((r) => (r.filter((row) => row.rowKey !== rowKey)));
    setSelectedRowKeys((prev) => prev.filter((key) => key !== rowKey));
  }, []);

  const toggleRowSelection = useCallback((rowKey, checked) => {
    setSelectedRowKeys((prev) => {
      const hasRow = prev.includes(rowKey);
      if (checked && !hasRow) {
        return [...prev, rowKey];
      }
      if (!checked && hasRow) {
        return prev.filter((key) => key !== rowKey);
      }
      return prev;
    });
  }, []);

  const removeSelectedRows = useCallback(() => {
    if (!Array.isArray(selectedRowKeys) || selectedRowKeys.length === 0) {
      return;
    }
    Modal.confirm({
      title: 'Remove selected assignments?',
      content: `Are you sure you want to remove ${selectedRowKeys.length} selected assignment${
        selectedRowKeys.length > 1 ? 's' : ''
      }?`,
      okText: 'Remove',
      cancelText: 'Cancel',
      zIndex: zIndex + 30,
      okButtonProps: {
        danger: true,
      },
      onOk: () => {
        const selected = new Set(selectedRowKeys);
        setRows((prevRows) => prevRows.filter((row) => !selected.has(row.rowKey)));
        setSelectedRowKeys([]);
      },
      onCancel: () => {},
    });
  }, [selectedRowKeys, zIndex]);

  useEffect(() => {
    const availableRowKeys = new Set(rows.map((row) => row.rowKey));
    setSelectedRowKeys((prev) => prev.filter((key) => availableRowKeys.has(key)));
  }, [rows]);

  const allRowKeys = useMemo(() => rows.map((row) => row.rowKey), [rows]);
  const selectedRowKeySet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys]);
  const selectedExistingRowsCount = useMemo(
    () => rows.reduce((count, row) => count + (selectedRowKeySet.has(row.rowKey) ? 1 : 0), 0),
    [rows, selectedRowKeySet],
  );
  const allRowsSelected = allRowKeys.length > 0 && selectedExistingRowsCount === allRowKeys.length;

  const toggleSelectAllRows = useCallback(
    (checked) => {
      if (!checked) {
        setSelectedRowKeys([]);
        return;
      }
      setSelectedRowKeys(allRowKeys);
    },
    [allRowKeys],
  );

  const handleSubmit = async () => {
    if (!classId || !currentUserId) {
      showAlert('Missing class or user.', 'error');
      return;
    }
    if (!Array.isArray(studentIds) || studentIds.length === 0) {
      showAlert('Select at least one student.', 'error');
      return;
    }
    const baseline = baselineByAssignmentRef.current || {};
    const baselineAssignmentKeys = Object.keys(baseline);
    const rowList = Array.isArray(rows) ? rows : [];
    for (let i = 0; i < rowList.length; i += 1) {
      if (!rowList[i]?.assignmentValue && rowHasAnyUserInput(rowList[i])) {
        showAlert(`Row ${i + 1}: assignment is required.`, 'error');
        return;
      }
    }
    const rowsWithAssignment = rowList.filter((r) => Boolean(r?.assignmentValue));
    if (rowsWithAssignment.length === 0 && baselineAssignmentKeys.length === 0) {
      onClose?.();
      return;
    }
    if (rowsWithAssignment.length > 0) {
      const keys = rowsWithAssignment.map((r) => r.assignmentValue);
      if (new Set(keys).size !== keys.length) {
        showAlert('Each assignment can only appear once.', 'error');
        return;
      }
    }
    const normalizedRows = [];
    for (let i = 0; i < rowsWithAssignment.length; i += 1) {
      const row = rowsWithAssignment[i];
      const n = i + 1;
      if (!row.dueDate) {
        showAlert(`Row ${n}: due date and time is required.`, 'error');
        return;
      }
      const parsedDueDate = dayjs(row.dueDate);
      if (!parsedDueDate.isValid()) {
        showAlert(`Row ${n}: due date and time is invalid.`, 'error');
        return;
      }
      if (row.attempts == null || row.attempts === '' || Number(row.attempts) < 1) {
        showAlert(`Row ${n}: attempts must be at least 1.`, 'error');
        return;
      }
      normalizedRows.push({ ...row, dueDate: parsedDueDate });
    }
    const currentKeySet = new Set(normalizedRows.map((r) => r.assignmentValue));
    const removes = [];
    Object.keys(baseline).forEach((assignmentValue) => {
      if (!currentKeySet.has(assignmentValue)) {
        const parsed = parseAssignmentValueKey(assignmentValue);
        if (parsed) {
          removes.push(parsed);
        }
      }
    });
    const creates = [];
    const updates = [];
    for (const row of normalizedRows) {
      const parsedKey = parseAssignmentValueKey(row.assignmentValue);
      if (!parsedKey) {
        showAlert('Invalid assignment selection.', 'error');
        return;
      }
      const scheduleAt = row.scheduleAt || dayjs().toISOString();
      const item = {
        assignmentType: parsedKey.assignmentType,
        assignmentId: parsedKey.assignmentId,
        dueDate: row.dueDate.toISOString(),
        attempts: Number(row.attempts),
        scheduleAt,
      };
      const kind = parsedKey.assignmentType === 'quiz' ? 'quiz' : 'activity';
      const id = parsedKey.assignmentId;
      if (kind === 'activity') {
        const raw = (mergedClassActivities || []).find((a) => String(a.id ?? a.activityId) === String(id));
        if (!raw) {
          showAlert(`Activity not found: ${id}`, 'error');
          return;
        }
        if (!classActivityMatchesActivitiesCatalog(raw, activityDefinitionIds)) {
          showAlert('This activity is not a valid assignment.', 'error');
          return;
        }
        const hasClass = (raw.classes || []).some((c) => String(c.id) === String(classId));
        if (!hasClass) {
          showAlert('This activity is not assigned to this class.', 'error');
          return;
        }
      } else {
        const raw = (mergedClassQuizzes || []).find((q) => String(q.id ?? q.quizId) === String(id));
        if (!raw) {
          showAlert(`Quiz not found: ${id}`, 'error');
          return;
        }
        if (!classQuizMatchesQuizzesCatalog(raw, quizDefinitionIds)) {
          showAlert('This quiz is not a valid assignment.', 'error');
          return;
        }
        const hasClass = (raw.classes || []).some((c) => String(c.id) === String(classId));
        if (!hasClass) {
          showAlert('This quiz is not assigned to this class.', 'error');
          return;
        }
      }
      const cur = comparableDiffSlice(row.dueDate, row.attempts, scheduleAt);
      const base = baseline[row.assignmentValue];
      if (!base) {
        creates.push(item);
      } else if (cur.due !== base.due || cur.att !== base.att || cur.sch !== base.sch) {
        updates.push(item);
      }
    }
    if (creates.length === 0 && updates.length === 0 && removes.length === 0) {
      showAlert('No changes to save.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const notificationsPayload = [];
      const studentRecipients = Array.isArray(studentIds) ? studentIds.slice(0, 1) : [];
      const receiversId = filter(classUsers, (user) => user?.role !== 'teacher' && user?.active === true).map(
        (user) => user.id,
      );
      for (const item of creates) {
        if (studentRecipients.length === 0) {
          break;
        }
        const id = item.assignmentId;
        if (item.assignmentType === 'activity') {
          const raw = (mergedClassActivities || []).find((a) => String(a.id ?? a.activityId) === String(id));
          if (!raw) {
            continue;
          }
          const displayName =
            get(raw, 'displayName') ||
            get(raw, 'data.displayName') ||
            get(raw, 'id') ||
            '';
          const chapter = get(raw, 'data.chapter') ?? get(raw, 'bookId');
          const page = get(raw, 'data.page') ?? get(raw, 'pageId') ?? get(raw, 'page');
          const definitionId = getClassActivityDefinitionId(raw) ?? id;
          const configAssignment = get(raw, 'data.config_assignment') || get(raw, 'config_assignment');
          const isEngageAssignment = configAssignment?.isFrom === 'engagement';
          notificationsPayload.push({
            id: uuidv4(),
            classId,
            displayName,
            senderId: currentUserId,
            receiversId,
            modified: dayjs().toISOString(),
            data: {
              chapter,
              page,
              id: definitionId,
              scheduleAt: item.scheduleAt,
              classes: [{ id: classId, users: studentRecipients }],
              assignfrom: null,
              ...(isEngageAssignment && configAssignment ? { config_assignment: configAssignment } : {}),
            },
            message: 'Your teacher Updated this Activity',
            type: 'update_activity',
            is_soy_story: false,
            userSeen: [],
          });
        } else {
          const raw = (mergedClassQuizzes || []).find((q) => String(q.id ?? q.quizId) === String(id));
          if (!raw) {
            continue;
          }
          const displayName =
            get(raw, 'displayName') ||
            get(raw, 'data.displayName') ||
            get(raw, 'id') ||
            '';
          const chapter = get(raw, 'data.chapter') ?? get(raw, 'bookId');
          const page = get(raw, 'data.page') ?? get(raw, 'pageId') ?? get(raw, 'page');
          const definitionId = getClassQuizDefinitionId(raw) ?? id;
          const configAssignment = get(raw, 'data.config_assignment') || get(raw, 'config_assignment');
          const isEngageAssignment = configAssignment?.isFrom === 'engagement';
          notificationsPayload.push({
            id: uuidv4(),
            classId,
            displayName,
            senderId: currentUserId,
            receiversId,
            modified: dayjs().toISOString(),
            data: {
              chapter,
              page,
              id: definitionId,
              scheduleAt: item.scheduleAt,
              classes: [{ id: classId, users: studentRecipients }],
              assignfrom: null,
              ...(isEngageAssignment && configAssignment ? { config_assignment: configAssignment } : {}),
            },
            message: 'Your teacher Assigned this Quiz',
            type: 'create_quiz',
            userSeen: [],
          });
        }
      }
      await dispatch(
        handleSaveStudentDifferentiationBulk({
          classId,
          studentUserIds: Array.isArray(studentIds) ? studentIds.slice(0, 1) : [],
          createdByUserId: currentUserId,
          changes: {
            create: creates,
            update: updates,
            remove: removes,
          },
        }),
      );
      if (notificationsPayload.length > 0) {
        try {
          await createBatchNotificationAssignment(notificationsPayload, usersByClass);
        } catch (notifyErr) {
          Sentry.captureException(notifyErr);
        }
      }
      if (firestore && studentIds[0]) {
        try {
          await deleteDoc(doc(firestore, draftCollectionId, `${classId}_${studentIds[0]}`));
          draftExistsRef.current = false;
        } catch (draftErr) {
          Sentry.captureException(draftErr);
        }
      }
      await Promise.all([
        dispatch(handleListClassActivities(classId)),
        dispatch(handleListClassQuizzes(classId)),
      ]);
      showAlert('Differentiation saved.', 'success');
      onCompleted?.();
      onClose?.();
    } catch (err) {
      Sentry.captureException(err);
      showAlert(err?.message || 'Save failed.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const draftSpinActive = loadingInitialDraft && !submitting;

  const handleCancelRequest = useCallback(() => {
    if (submitting) return;
    if (hasUnsavedAssignmentChanges) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Do you want to close without saving?',
        okText: 'Close',
        cancelText: 'Cancel',
        zIndex: zIndex + 30,
        okButtonProps: {
          className: 'force-blue-button',
          style: {
            backgroundColor: '#012B70',
            borderColor: '#012B70',
          },
        },
        onOk: () => {
          onClose?.();
        },
        onCancel: () => {},
      });
      return;
    }
    onClose?.();
  }, [hasUnsavedAssignmentChanges, onClose, submitting]);

  const modalTitle = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        paddingRight: 4,
        flexWrap: 'wrap',
      }}
    >
      <span>Student differentiation</span>
      {hasUnsavedAssignmentChanges ? (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: 999,
            background: '#fff3cd',
            border: '1px solid #ffe58f',
            color: '#ad6800',
            fontSize: 12,
            fontWeight: 600,
            padding: '2px 10px',
            lineHeight: 1.4,
          }}
        >
          Draft
        </span>
      ) : null}
      <Tooltip
        title="In the assignment list, the green checkmark means the selected student already has differentiation on that assignment. You can still pick it to update due date or attempts."
        placement="bottom-start"
        PopperProps={tooltipPopperProps}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
            color: '#595959',
            fontSize: 13,
            fontWeight: 400,
            maxWidth: 280,
            marginRight: 20,
          }}
        >
          <InfoOutlined style={{ fontSize: 18 }} />
        </span>
      </Tooltip>
    </div>
  );

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={handleCancelRequest}
      onOk={handleSubmit}
      okText={hasExistingDifferentiation ? 'Update' : 'Save'}
      confirmLoading={submitting}
      width={720}
      destroyOnClose
      zIndex={zIndex}
      styles={{
        body: modalBodyStyle,
      }}
    >
      <Spin spinning={draftSpinActive} tip="Loading draft…" size="large" style={{ width: '100%', minHeight: 120 }}>
      <Row gutter={[0, 16]} style={{ width: '100%' }}>
        <Col span={24} className="floatingLabelBG">
          <Autocomplete
            fullWidth
            options={studentOptions}
            value={selectedStudentOption}
            disableClearable
            isOptionEqualToValue={(option, value) =>
              option != null && value != null && String(option.id) === String(value.id)
            }
            getOptionLabel={(option) => option?.label || ''}
            onOpen={() => {
              setDatePickerRenderKey((k) => k + 1);
            }}
            onChange={(e, option) => {
              if (!option || option.id == null) {
                return;
              }
              seededStudentRef.current = null;
              setStudentIds([option.id]);
            }}
            noOptionsText="No students found"
            renderOption={(props, option, { selected }) => (
              <li
                {...props}
                key={option.id}
                style={{
                  ...props.style,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span style={{ width: 18, display: 'inline-flex', justifyContent: 'center' }}>
                  {selected ? <Check sx={{ fontSize: 16, color: '#50a155' }} /> : null}
                </span>
                {option.label}
              </li>
            )}
            slotProps={{
              paper: {
                sx: {
                  zIndex: popperZ,
                  border: '1px solid #e6e6e6',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                },
              },
              popper: {
                sx: { zIndex: popperZ },
              },
            }}
            sx={{
              ...compactStudentsFieldSx,
              '& .MuiAutocomplete-inputRoot': {
                minHeight: 48,
                alignItems: 'center',
                paddingTop: '4px !important',
                paddingBottom: '4px !important',
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select student"
                variant="outlined"
                placeholder="Search and select a student"
              />
            )}
          />
        </Col>
      </Row>

      <div style={{ overflow: 'visible', width: '100%', marginTop: 10 }}>
        {rows.length > 0 ? (
          <Row gutter={[8, 8]} style={{ width: '100%', marginBottom: 10, display: 'flex', justifyContent: 'flex-end' }}>
            <Col>
              <Button
                variant="outlined"
                size="small"
                sx={rowActionButtonSx}
                onClick={() => toggleSelectAllRows(!allRowsSelected)}
              >
                {allRowsSelected ? 'Unselect all' : 'Select all'}
              </Button>
            </Col>
            <Col>
              <Button
                color="error"
                variant="outlined"
                size="small"
                sx={rowActionButtonSx}
                onClick={removeSelectedRows}
                disabled={selectedRowKeys.length === 0}
              >
                Remove selected
              </Button>
            </Col>
          </Row>
        ) : null}
        <div style={rowScrollContainerStyle}>
        <Row gutter={[0, 14]} style={{ width: '100%' }}>
          {rows.map((row, index) => (
            <Col span={24} key={row.rowKey}>
              <div style={rowCardStyle}>
                <div style={rowHeaderStyle}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Checkbox
                      size="small"
                      checked={selectedRowKeys.includes(row.rowKey)}
                      onChange={(e) => toggleRowSelection(row.rowKey, e.target.checked)}
                    />
                    {`Assignment ${index + 1}`}
                  </span>
                  <Button color="error" onClick={() => removeRow(row.rowKey)} size="small" variant="text">
                    Remove
                  </Button>
                </div>
                <Row gutter={[12, 14]} align="middle">
                  <Col xs={24} sm={24} md={10} className="floatingLabelBG">
                    {(() => {
                      const assignmentRowOptions = buildAssignmentAutocompleteOptions(
                        getOptionsForRow,
                        row.rowKey,
                      );
                      const selectedAssignmentOption =
                        row.assignmentValue == null || row.assignmentValue === ''
                          ? null
                          : assignmentRowOptions.find((o) => o.value === row.assignmentValue) ??
                            (assignmentOptionByValue[row.assignmentValue]
                              ? {
                                  ...assignmentOptionByValue[row.assignmentValue],
                                  groupSortKey: assignmentOptionByValue[row.assignmentValue].isPastDue
                                    ? '2'
                                    : '1',
                                }
                              : null);
                      const closedInputLabel = selectedAssignmentOption?.label || '';
                      const closedTruncated = truncateLabel(closedInputLabel, 30);
                      const showAssignmentFieldTooltip =
                        Boolean(selectedAssignmentOption) &&
                        closedTruncated.truncated &&
                        assignmentDropdownOpenRowKey !== row.rowKey;
                      return (
                        <Tooltip
                          title={showAssignmentFieldTooltip ? closedInputLabel : ''}
                          placement="top"
                          PopperProps={tooltipPopperProps}
                          disableFocusListener
                          disableHoverListener={!showAssignmentFieldTooltip}
                          disableTouchListener={!showAssignmentFieldTooltip}
                        >
                          <Autocomplete
                            fullWidth
                            disableClearable
                            options={assignmentRowOptions}
                            value={selectedAssignmentOption}
                            groupBy={(option) => option.groupSortKey}
                            filterOptions={assignmentOptionFilter}
                            isOptionEqualToValue={(option, value) =>
                              option != null && value != null && option?.value === value?.value
                            }
                            getOptionLabel={(option) => option?.label || ''}
                            onChange={(e, option) => {
                              updateRow(row.rowKey, {
                                assignmentValue: option?.value ?? null,
                              });
                            }}
                            onOpen={() => {
                              setAssignmentDropdownOpenRowKey(row.rowKey);
                              setDatePickerRenderKey((k) => k + 1);
                            }}
                            onClose={() => {
                              setAssignmentDropdownOpenRowKey((prev) =>
                                prev === row.rowKey ? null : prev,
                              );
                            }}
                            noOptionsText="No assignments found"
                            slotProps={assignmentAutocompleteSlotProps}
                            sx={[
                              compactRowFieldSx,
                              assignmentAutocompleteFieldSx,
                              selectedAssignmentOption && {
                                '& .MuiInputBase-input::placeholder': {
                                  opacity: '0 !important',
                                },
                              },
                            ]}
                            renderGroup={(params) => (
                              <li key={params.key}>
                                {params.group ? (
                                  <div
                                    role="presentation"
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      color: '#666',
                                      padding: '8px 16px 4px',
                                    }}
                                  >
                                    {params.group === '1' ? 'Active' : 'Past Due'}
                                  </div>
                                ) : null}
                                <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                              </li>
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.value}>
                                  <span
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      width: '100%',
                                      minWidth: 0,
                                    }}
                                  >
                                    {option.hasExistingDiff || row.assignmentValue === option.value ? (
                                      <CheckCircle
                                        sx={{ fontSize: 20, color: '#2e7d32', flexShrink: 0 }}
                                        aria-hidden
                                      />
                                    ) : (
                                      <span style={{ width: 20, flexShrink: 0 }} aria-hidden />
                                    )}
                                    <Tooltip
                                      title={option.label}
                                      placement="top"
                                      PopperProps={tooltipPopperProps}
                                      disableHoverListener={option.label.length <= 45}
                                      disableTouchListener={option.label.length <= 45}
                                    >
                                      <span style={{ flex: 1, minWidth: 0 }}>
                                        <span style={ellipsisTextStyle}>
                                          {truncateLabel(option.label, 45).text}
                                        </span>
                                      </span>
                                    </Tooltip>
                                  </span>
                                </li>
                            )}
                            renderInput={(params) => {
                              const assignmentPlaceholder = selectedAssignmentOption
                                ? ''
                                : 'Select or search assignment';
                              return (
                                <TextField
                                  {...params}
                                  label="Assignment"
                                  variant="outlined"
                                  placeholder={assignmentPlaceholder}
                                  inputProps={{
                                    ...params.inputProps,
                                    placeholder: assignmentPlaceholder,
                                  }}
                                  InputLabelProps={{
                                    ...params.InputLabelProps,
                                    shrink: true,
                                  }}
                                />
                              );
                            }}
                          />
                        </Tooltip>
                      );
                    })()}
                  </Col>
                  <Col xs={24} sm={12} md={10} className="floatingLabelBG">
                    <div style={{ marginTop: -16 }}>
                      <CustomDateTimePicker
                      key={`${row.rowKey}-${datePickerRenderKey}`}
                        id={`dueDate-${row.rowKey}`}
                        label="Due day/time"
                        inputVariant="outlined"
                        InputLabelProps={{ shrink: true }}
                        format="MM-dd-yyyy hh:mm a"
                        defaultValue={null}
                        initialFocusedDate={dayjs().toString()}
                        value={row.dueDate ? dayjs(row.dueDate) : null}
                        onChange={(v) => updateRow(row.rowKey, { dueDate: v })}
                        popupAboveModal
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={4} className="floatingLabelBG">
                    <TextField
                      fullWidth
                      type="number"
                      label="Attempts"
                      variant="outlined"
                      value={row.attempts ?? ''}
                      onKeyDown={(e) => {
                        if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          updateRow(row.rowKey, { attempts: null });
                          return;
                        }
                        const sanitized = String(raw).replace(/\D/g, '');
                        if (sanitized === '') {
                          updateRow(row.rowKey, { attempts: null });
                          return;
                        }
                        const num = Number(sanitized);
                        if (!Number.isFinite(num)) {
                          updateRow(row.rowKey, { attempts: null });
                          return;
                        }
                        const clampedAttempts = Math.min(99, num);
                        updateRow(row.rowKey, { attempts: clampedAttempts });
                      }}
                      inputProps={{ min: 1, max: 99, inputMode: 'numeric', pattern: '[0-9]*' }}
                      sx={compactRowFieldSx}
                    />
                  </Col>
                </Row>
              </div>
            </Col>
          ))}
        </Row>
        </div>
      </div>

      <Row gutter={[0, 8]} style={{ width: '100%', marginTop: 8 }}>
        <Col span={24}>
          <Button fullWidth onClick={addRow} variant="outlined">
            Add another assignment
          </Button>
        </Col>
      </Row>
      </Spin>
    </Modal>
  );
};

export default StudentDifferentiationBulkModal;
